import { SwipeableDrawer } from '@mui/material';
import { t } from 'i18next';
import { CSSProperties, useState } from 'react';
import { useSelector } from 'react-redux';
import { FaXMarkLightSvgIcon } from '../../../assets/icons/svgs';
import DrawerCloseTopLeftButton from '../../Modals/Drawers/DrawerCloseTopLeftButton';
import DrawerPuller from '../../Modals/Drawers/DrawerPuller';
import useDrawerOpenClose from '../../Modals/Drawers/hooks/useDrawerOpenClose';
import {
  DRAWER_TRANSITION,
  DRAWER_TRANSITION_DURATION,
} from '../../Modals/Drawers/utils';
import ModalBigTitleBar from '../../Modals/ModalBigTitleBar';
import {
  drawerFromBottomPaperFullHeightStyles,
  modalCloseIconStyle,
  modalPaperRootBackdropStyles,
} from '../../Modals/utils';
import { TOP_MENU_BAR_HEIGHT } from '../../TopMenuBar.tsx/constants';
import { RootState } from '../../../redux/store';
import { colors } from '../../../theme/colors';
import {
  flexCenteredContentContainerStyles,
  flexCenteredParentContainerStyles,
} from '../../../theme/containers';
import spacing, { isMobileScreen } from '../../../theme/spacing';
import { fontSize } from '../../../theme/typography';
import { useLocationSelectorContext } from '../LocationSelectorContext';
import LocationRemoveSelectItem from './LocationRemoveSelectItem';
import RemoveUserLocationsButton from './RemoveUserLocationsButton';

interface UserLocationsRemovalModalProps {
  isOpen: boolean;
  handleClose: () => void;
}

const bottomMenuStyles: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: TOP_MENU_BAR_HEIGHT,
  minHeight: TOP_MENU_BAR_HEIGHT,
  borderTop: `1px solid ${colors.backgroundLight}`,
  padding: `${spacing.xs} ${spacing.medium}`,
};

const UserLocationsRemovalModal = () => {
  const { isRemoveLocationModalOpen, setIsRemoveLocationModalOpen } =
    useLocationSelectorContext();

  const isOpen = isRemoveLocationModalOpen;
  const handleClose = () => setIsRemoveLocationModalOpen(false);

  const { isDrawerOpen, setDrawerToClose, drawerOpacity } = useDrawerOpenClose({
    isOpen,
    handleManualClose: handleClose,
  });

  const Content = () => {
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    const userLocations = useSelector(
      (state: RootState) => state.user.locations,
    );

    const toggleLocationSelection = (id: string) => {
      const newSelectedIds = new Set(selectedIds);

      if (newSelectedIds.has(id)) {
        newSelectedIds.delete(id);
      } else {
        newSelectedIds.add(id);
      }

      setSelectedIds(newSelectedIds);
    };

    const LocationsList = () => {
      return (
        <div>
          {userLocations.map((location, index) => (
            <LocationRemoveSelectItem
              key={location.id}
              location={location}
              isSelected={selectedIds.has(location.id)}
              toggleSelection={toggleLocationSelection}
              isLastItem={index === userLocations.length - 1}
            />
          ))}
        </div>
      );
    };

    const BottomMenu = () => {
      return (
        <div style={bottomMenuStyles}>
          {selectedIds.size > 0 ? (
            <RemoveUserLocationsButton
              onComplete={() => handleClose()}
              locationsIds={Array.from(selectedIds)}
            />
          ) : (
            <div
              style={{
                color: colors.textGreyMedium,
                fontSize: fontSize.md,
                textAlign: 'center',
              }}
            >
              {t('location.selectLocationsToRemove')}
            </div>
          )}
        </div>
      );
    };

    return (
      <>
        <div
          style={{
            ...flexCenteredParentContainerStyles,
            padding: spacing.medium,
          }}
        >
          <div style={flexCenteredContentContainerStyles}>
            <div
              style={{
                width: '100%',
              }}
            >
              <LocationsList />
            </div>
          </div>
        </div>
        <BottomMenu />
      </>
    );
  };

  return (
    <SwipeableDrawer
      onClose={setDrawerToClose}
      anchor="bottom"
      onOpen={() => console.log('')}
      open={isDrawerOpen}
      transitionDuration={DRAWER_TRANSITION_DURATION}
      disableBackdropTransition
      sx={{
        opacity: drawerOpacity,
        transition: DRAWER_TRANSITION,
        '& .MuiPaper-root': {
          ...drawerFromBottomPaperFullHeightStyles,
          backgroundColor: colors.backgroundMedium,
        },
        '& .MuiBackdrop-root': {
          ...modalPaperRootBackdropStyles,
          pointerEvents: isDrawerOpen ? 'auto' : 'none',
        },
      }}
    >
      {isDrawerOpen && (
        <>
          <DrawerCloseTopLeftButton />
          <FaXMarkLightSvgIcon
            onClick={setDrawerToClose}
            className="onClickEffect"
            size={fontSize['2xl']}
            style={{
              ...modalCloseIconStyle,
              visibility: isMobileScreen ? 'hidden' : 'visible',
              animation: 'fadeInOnRender 2.5s',
            }}
          />
        </>
      )}

      <DrawerPuller onClose={setDrawerToClose} isFullHeightDrawer={true} />

      <ModalBigTitleBar
        onModalClose={setDrawerToClose}
        title={`${t('location.removeLocations')}`}
      />

      <Content />
    </SwipeableDrawer>
  );
};

export default UserLocationsRemovalModal;
